/**
 * AI360SmartSearch Widget
 * Main widget component integrating all features
 */

import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import type { WidgetConfig } from './types';
import { createApiClient } from './services/api-client';
import { useWidgetStore } from './store/widgetStore';
import { useViewStore } from './store/viewStore';
import { SearchBox } from './components/SearchBox';
import { ResultsList } from './components/ResultsList';
import { VoiceController } from './components/voice/VoiceController';
import { ErrorBoundary, ErrorMessage } from './components/ErrorBoundary';
import { useOffline } from './hooks/useOffline';
import './styles/main.css';
import './widget.css';

interface WidgetProps {
  config: WidgetConfig;
}

// Helper function to determine if voice position should show floating controller
const isFloatingVoicePosition = (position?: string): boolean => {
  const floatingPositions = ['bottom', 'bottom-left', 'bottom-right', 'bottom-center'];
  return floatingPositions.includes(position || 'bottom');
};

const Widget: React.FC<WidgetProps> = ({ config }) => {
  const {
    query,
    results,
    isSearching,
    searchError,
    voiceState,
    theme,
    setQuery,
    search,
    setVoiceState,
    setTheme,
  } = useWidgetStore();

  const { initializeFromSettings } = useViewStore();
  const { isOffline } = useOffline();
  const [isVoiceOpen, setIsVoiceOpen] = useState(false);

  // Initialize API client
  useEffect(() => {
    createApiClient({
      baseUrl: config.backendUrl,
      apiKey: config.apiKey,
      apiType: config.apiType || 'ai360api',
    });
  }, [config.backendUrl, config.apiKey, config.apiType]);

  // Set theme
  useEffect(() => {
    if (config.theme) {
      setTheme(config.theme);
    }
  }, [config.theme, setTheme]);

  // Initialize viewStore from config
  useEffect(() => {
    initializeFromSettings(config);
  }, [config, initializeFromSettings]);

  // Handle voice transcript
  const handleVoiceTranscript = (text: string) => {
    setQuery(text);
    search(text);
  };

  // Handle voice button click in search box
  const handleVoiceClick = () => {
    setIsVoiceOpen(true);
  };

  // Map voice position from backend config
  const getVoicePosition = (): 'bottom-right' | 'bottom-left' | 'bottom-center' => {
    const pos = config.voiceButtonPosition || config.voicePosition || 'right';
    const posMap: Record<string, 'bottom-right' | 'bottom-left' | 'bottom-center'> = {
      'left': 'bottom-left',
      'center': 'bottom-center', 
      'right': 'bottom-right',
      'bottom-left': 'bottom-left',
      'bottom-center': 'bottom-center',
      'bottom-right': 'bottom-right',
    };
    return posMap[pos] || 'bottom-right';
  };

  return (
    <ErrorBoundary>
      <div className="ai360-widget" data-theme={theme}>
        <div className="ai360-widget-container">
          {isOffline && (
            <div className="ai360-widget-offline-banner">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path d="M8 5v3m0 3v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>Sin conexión - Mostrando resultados en caché</span>
            </div>
          )}

          <div className="ai360-widget-search">
            <SearchBox
              placeholder={config.placeholder || 'Escribe tu consulta...'}
              onSearch={search}
              onVoiceClick={config.voiceEnabled ? handleVoiceClick : undefined}
              showVoiceButton={config.voiceEnabled}
              debounceMs={config.debounceMs}
              config={config}
            />
          </div>

          <div className="ai360-widget-results">
            {searchError && (
              <ErrorMessage
                title="Error en la búsqueda"
                message={searchError.message}
                onRetry={() => search(query)}
              />
            )}

            <ResultsList config={config} />
          </div>

          {/* Only show VoiceController floating bubble when position is 'bottom' or similar */}
          {config.voiceEnabled && isFloatingVoicePosition(config.voiceButtonPosition) && (
            <VoiceController
              layout={config.voiceLayout || 'bubble'}
              position={getVoicePosition()}
              onTranscript={handleVoiceTranscript}
              onStateChange={setVoiceState}
              silenceThreshold={config.silenceThreshold || 2000}
              language={config.voiceLanguage || config.locale || 'es-ES'}
            />
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Widget;

// Global API for mounting/unmounting
declare global {
  interface Window {
    Ai360SmartSearchWidget?: {
      mount: (selector: string, config: WidgetConfig) => void;
      unmount: (selector: string) => void;
      setTheme: (theme: 'light' | 'dark' | 'auto') => void;
      search: (query: string) => Promise<void>;
    };
    ai360Config?: Partial<WidgetConfig>;
    ai360smartsearchSettings?: Partial<WidgetConfig>;
  }
}

if (typeof window !== 'undefined') {
  const roots = new Map<string, ReturnType<typeof createRoot>>();

  window.Ai360SmartSearchWidget = {
    mount: (selector: string, config: WidgetConfig) => {
      const container = document.querySelector(selector);
      if (!container) {
        console.error(`[AI360SmartSearch] Container not found: ${selector}`);
        return;
      }

      // Unmount if already mounted
      if (roots.has(selector)) {
        window.Ai360SmartSearchWidget?.unmount(selector);
      }

      // Merge config with WordPress settings (ai360smartsearchSettings) and fallback to ai360Config
      const wpSettings = window.ai360smartsearchSettings || {};
      const globalConfig = window.ai360Config || {};
      
      const mergedConfig: WidgetConfig = {
        ...config,
        ...globalConfig,
        ...wpSettings,  // WordPress settings have priority
      };

      // Create root and render
      const root = createRoot(container);
      root.render(<Widget config={mergedConfig} />);
      roots.set(selector, root);

      console.log('[AI360SmartSearch] Widget mounted successfully');
    },

    unmount: (selector: string) => {
      const root = roots.get(selector);
      if (root) {
        root.unmount();
        roots.delete(selector);
        console.log('[AI360SmartSearch] Widget unmounted');
      }
    },

    setTheme: (theme: 'light' | 'dark' | 'auto') => {
      useWidgetStore.getState().setTheme(theme);
    },

    search: async (query: string) => {
      await useWidgetStore.getState().search(query);
    },
  };
}

// Export for module systems - ensures the global is accessible
export const Ai360SmartSearchWidget = typeof window !== 'undefined' ? window.Ai360SmartSearchWidget : null;
